// The map editor's model: pure operations on a MapData (no DOM, no three). Every edit returns a new map,
// so undo is a stack of whole maps. The author edits `half` (mirrored on load) and `center`, whose items
// are kept symmetric as a set here: an edited centre item always carries its mirror partner with it.
import { validateMap, MapValidationError, HALF_TURN_KEYS, type BuildingStyle, type DecorKind, type MapData, type MapIssue, type MapObjects, type Patch, type Prop, type PropKind, type Pt, type RoadKind, type WallStyle } from '../../core/map/schema.js';
import { PROP_SHAPES } from '../../core/layout.js';
import { expandMap, inAuthoredHalf, mirrorObjects, mirrorPt } from '../../core/map/expand.js';
import { distPolyline, distSeg } from '../../core/map/geometry.js';

export type ObjKey = keyof MapObjects;
export type Part = 'half' | 'center';
export type Item = MapObjects[ObjKey][number];
export type ObjRef = { kind: 'obj'; part: Part; key: ObjKey; index: number };
export type SpecialKind = 'base' | 'side' | 'den';
export type Ref = { kind: SpecialKind } | ObjRef;

export const POINT_KEYS = ['camps', 'bridges', 'trees', 'rocks', 'pillars'] as const satisfies readonly ObjKey[];
const LINE_KEYS = ['walls', 'streams', 'roads', 'cliffs'] as const satisfies readonly ObjKey[];
/* Same slack the validator gives a hand-placed point on the diagonal. */
const HALF_TOL = 0.05;

export const isPointKey = (key: ObjKey) => (POINT_KEYS as readonly ObjKey[]).includes(key);
export const hasVertices = (key: ObjKey) => key === 'ponds' || (LINE_KEYS as readonly ObjKey[]).includes(key);
export const minVertices = (key: ObjKey, item: Item) => isClosed(key, item) ? 3 : 2;
export const isClosed = (key: ObjKey, item: Item) => key === 'ponds' || (key === 'cliffs' && (item as { closed: boolean }).closed);

/* Centimetre precision keeps saved files readable; negation stays exact, so mirrors stay exact too. */
const cm = (v: number) => Math.round(v * 100) / 100 + 0;
const cmPt = ([x, z]: Pt): Pt => [cm(x), cm(z)];

export function snapPt([x, z]: Pt, step: number): Pt {
  return step > 0 ? cmPt([Math.round(x / step) * step, Math.round(z / step) * step]) : cmPt([x, z]);
}

export function vertices(key: ObjKey, item: Item): Pt[] | null {
  if (key === 'ponds') return item as Pt[];
  if ((LINE_KEYS as readonly ObjKey[]).includes(key)) return (item as { points: Pt[] }).points;
  return null;
}

export function withVertices(key: ObjKey, item: Item, pts: Pt[]): Item {
  return key === 'ponds' ? pts : { ...(item as object), points: pts } as Item;
}

const centroid = (pts: Pt[]): Pt => [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];

/** Where an item sits for the half rule — the validator's anchor: a vertex centroid for lines and polygons. */
export function anchor(key: ObjKey, item: Item): Pt {
  const pts = vertices(key, item);
  if (pts) return centroid(pts);
  if (Array.isArray(item)) return item as Pt;
  const o = item as { x: number; z: number };
  return [o.x, o.z];
}

export function translate(key: ObjKey, item: Item, dx: number, dz: number): Item {
  const pts = vertices(key, item);
  if (pts) return withVertices(key, item, pts.map(([x, z]) => cmPt([x + dx, z + dz])));
  if (Array.isArray(item)) return cmPt([(item as Pt)[0] + dx, (item as Pt)[1] + dz]);
  const o = item as { x: number; z: number };
  return { ...o, x: cm(o.x + dx), z: cm(o.z + dz) } as Item;
}

/** Moves the item so its anchor lands on p. */
export const moveTo = (key: ObjKey, item: Item, [x, z]: Pt): Item => {
  const [ax, az] = anchor(key, item);
  return translate(key, item, x - ax, z - az);
};

const turn = (a: number) => Math.round((((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) * 1e4) / 1e4;

/** Turns the item by `da` radians: its rot for rectangles, ellipses and decor, its vertices about their
    centroid for lines and polygons; a bare point has no orientation. */
export function rotate(key: ObjKey, item: Item, da: number): Item {
  const pts = vertices(key, item);
  if (pts) {
    const [cx, cz] = centroid(pts), c = Math.cos(da), s = Math.sin(da);
    return withVertices(key, item, pts.map(([x, z]) => cmPt([cx + (x - cx) * c - (z - cz) * s, cz + (x - cx) * s + (z - cz) * c])));
  }
  if (Array.isArray(item)) return item;
  const o = item as { rot: number };
  return { ...o, rot: turn(o.rot + da) } as Item;
}

/** Grows the item by factor k along what "size" means for it: a building's, field's or ford's w/d, a
    brush's rx/rz, a decor's scale, a stream's or road's width. Other kinds (props included: their footprint
    is fixed by kind) have no size and come back unchanged. */
export function resize(key: ObjKey, item: Item, k: number): Item {
  const o = item as Record<string, number>;
  const grow = (v: number) => Math.max(0.1, cm(v * k));
  if (key === 'buildings' || key === 'fields' || key === 'fords') return { ...o, w: grow(o.w!), d: grow(o.d!) } as unknown as Item;
  if (key === 'brush') return { ...o, rx: grow(o.rx!), rz: grow(o.rz!) } as unknown as Item;
  if (key === 'decor') return { ...o, scale: grow(o.scale!) } as unknown as Item;
  if (key === 'streams' || key === 'roads') return { ...o, width: grow(o.width!) } as unknown as Item;
  return item;
}

function emptyObjects(): MapObjects {
  return { camps: [], bridges: [], trees: [], rocks: [], pillars: [], walls: [], buildings: [], streams: [], ponds: [], cliffs: [], brush: [], roads: [], decor: [], props: [], fields: [], fords: [] };
}

/** The item's copy on the other half, exactly as expand.ts makes it. */
export function mirrorItem(key: ObjKey, item: Item): Item {
  const o = emptyObjects();
  (o[key] as Item[]).push(item);
  return (mirrorObjects(o)[key] as Item[])[0]!;
}

const EPS = 1e-6;
const nearPts = (a: Pt[], b: Pt[]) => a.length === b.length && a.every((p, i) => Math.abs(p[0] - b[i]![0]) <= EPS && Math.abs(p[1] - b[i]![1]) <= EPS);
function samePts(a: Pt[], b: Pt[], closed: boolean) {
  const rev = [...b].reverse();
  if (!closed) return nearPts(a, b) || nearPts(a, rev);
  const n = b.length, shift = (arr: Pt[], s: number) => arr.map((_, i) => arr[(i + s) % n]!);
  for (let s = 0; s < n; s++) if (nearPts(a, shift(b, s)) || nearPts(a, shift(rev, s))) return true;
  return false;
}
const angleNear = (a: number, b: number, period: number) => {
  const d = ((a - b) % period + period) % period;
  return Math.min(d, period - d) <= EPS;
};

/** The validator's notion of "the same centre object": lines either way round, closed shapes from any vertex,
    rectangles (buildings, fields, fords) modulo a half turn, decor and props modulo a full one. */
export function sameItem(key: ObjKey, a: Item, b: Item): boolean {
  const pa = vertices(key, a), pb = vertices(key, b);
  if (pa && pb) {
    if (!samePts(pa, pb, isClosed(key, a))) return false;
    if (key === 'ponds') return true;
    const { points: _a, ...ra } = a as { points: Pt[] }, { points: _b, ...rb } = b as { points: Pt[] };
    return JSON.stringify(ra) === JSON.stringify(rb);
  }
  if (Array.isArray(a)) return nearPts([a as Pt], [b as Pt]);
  const oa = a as Record<string, number | string>, ob = b as Record<string, number | string>;
  return Object.keys(oa).every(k => {
    if (k === 'rot' && key !== 'brush') return angleNear(oa[k] as number, ob[k] as number, HALF_TURN_KEYS.includes(key) ? Math.PI : 2 * Math.PI);
    return typeof oa[k] === 'number' ? Math.abs((oa[k] as number) - (ob[k] as number)) <= EPS : oa[k] === ob[k];
  });
}

export const selfSymmetric = (key: ObjKey, item: Item) => sameItem(key, item, mirrorItem(key, item));

/** Index of the centre item that is `i`'s mirror (i itself when it is self-symmetric), or -1. */
export function partnerIndex(map: MapData, key: ObjKey, i: number): number {
  const list = map.center[key] as Item[], item = list[i]!;
  if (selfSymmetric(key, item)) return i;
  const m = mirrorItem(key, item);
  return list.findIndex((other, j) => j !== i && sameItem(key, other, m));
}

export function getItem(map: MapData, ref: ObjRef): Item {
  return (map[ref.part][ref.key] as Item[])[ref.index]!;
}

export function specialPos(map: MapData, kind: SpecialKind): Pt {
  return kind === 'base' ? map.base : kind === 'side' ? map.points.side : map.den.pos;
}

export function setSpecial(map: MapData, kind: SpecialKind, p: Pt): MapData {
  const m = structuredClone(map), q = cmPt(p);
  if (kind === 'base') m.base = q;
  else if (kind === 'side') m.points.side = q;
  else m.den.pos = q;
  return m;
}

/** Places a side point or den where clicked, taking the mirrored spot when the click is on the blue half. */
export function placeSpecial(map: MapData, kind: SpecialKind, p: Pt): MapData {
  return setSpecial(map, kind, kind !== 'base' && !inAuthoredHalf(map, p, HALF_TOL) ? mirrorPt(p) : p);
}

/** Replaces the item behind ref. A centre item's old partner goes and the new item's partner comes in,
    so the centre stays symmetric whatever the edit; the returned ref follows the item. */
export function setItem(map: MapData, ref: ObjRef, item: Item): { map: MapData; ref: ObjRef } {
  const m = structuredClone(map);
  const list = m[ref.part][ref.key] as Item[];
  if (ref.part === 'half') {
    list[ref.index] = item;
    return { map: m, ref };
  }
  const p = partnerIndex(map, ref.key, ref.index);
  let index = ref.index;
  if (p !== -1 && p !== index) { list.splice(p, 1); if (p < index) index--; }
  list[index] = item;
  if (!selfSymmetric(ref.key, item)) list.push(mirrorItem(ref.key, item));
  return { map: m, ref: { ...ref, index } };
}

/** Adds an item to the authored half — as its mirror when it was placed on the blue half — or to the centre with its partner. */
export function addItem(map: MapData, key: ObjKey, item: Item, part: Part = 'half'): { map: MapData; ref: ObjRef } {
  const m = structuredClone(map);
  const list = m[part][key] as Item[];
  if (part === 'half') {
    list.push(inAuthoredHalf(map, anchor(key, item), HALF_TOL) ? item : mirrorItem(key, item));
  } else {
    list.push(item);
    if (!selfSymmetric(key, item)) list.push(mirrorItem(key, item));
  }
  return { map: m, ref: { kind: 'obj', part, key, index: list.length - (part === 'center' && !selfSymmetric(key, item) ? 2 : 1) } };
}

export function deleteItem(map: MapData, ref: ObjRef): MapData {
  const m = structuredClone(map);
  const list = m[ref.part][ref.key] as Item[];
  const p = ref.part === 'center' ? partnerIndex(map, ref.key, ref.index) : -1;
  for (const i of [ref.index, p].filter(i => i >= 0).sort((a, b) => b - a).filter((v, k, a) => a.indexOf(v) === k)) list.splice(i, 1);
  return m;
}

export function duplicateItem(map: MapData, ref: ObjRef, offset: Pt = [2, 2]): { map: MapData; ref: ObjRef } {
  return addItem(map, ref.key, translate(ref.key, getItem(map, ref), offset[0], offset[1]), ref.part);
}

/** Moves an item between the authored half and the centre. Into the centre it takes its partner along
    (none when it is its own mirror); back out, the pair collapses to whichever copy is on the red half. */
export function toggleCenter(map: MapData, ref: ObjRef): { map: MapData; ref: ObjRef } {
  const item = getItem(map, ref);
  const rest = deleteItem(map, ref);
  if (ref.part === 'half') return addItem(rest, ref.key, item, 'center');
  return addItem(rest, ref.key, item, 'half');
}

/** Whether offering "centre" for this item makes sense: it already is one, or it sits close to point B. */
export function canToggleCenter(map: MapData, ref: ObjRef, reach = 12): boolean {
  if (ref.part === 'center') return true;
  const [x, z] = anchor(ref.key, getItem(map, ref));
  return Math.hypot(x, z) <= reach;
}

/* ---- vertices ---- */

export function moveVertex(map: MapData, ref: ObjRef, i: number, p: Pt): { map: MapData; ref: ObjRef } {
  const item = getItem(map, ref), pts = [...vertices(ref.key, item)!];
  pts[i] = cmPt(p);
  return setItem(map, ref, withVertices(ref.key, item, pts));
}

/** Inserts p as a new vertex after vertex `after`. */
export function insertVertex(map: MapData, ref: ObjRef, after: number, p: Pt): { map: MapData; ref: ObjRef } {
  const item = getItem(map, ref), pts = [...vertices(ref.key, item)!];
  pts.splice(after + 1, 0, cmPt(p));
  return setItem(map, ref, withVertices(ref.key, item, pts));
}

/** Null when the shape is already at its minimum (2 points for a line, 3 for a polygon). */
export function deleteVertex(map: MapData, ref: ObjRef, i: number): { map: MapData; ref: ObjRef } | null {
  const item = getItem(map, ref), pts = [...vertices(ref.key, item)!];
  if (pts.length <= minVertices(ref.key, item)) return null;
  pts.splice(i, 1);
  return setItem(map, ref, withVertices(ref.key, item, pts));
}

/* ---- hit testing (authored objects only: the mirrored half is not interactive) ---- */

const POINT_R: Record<string, number> = { camps: 1.5, bridges: 2.5, trees: 1.2, rocks: 1.1, pillars: 0.8 };
const SPECIAL_R = 2;

function inPolygon(x: number, z: number, pts: Pt[]) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, zi] = pts[i]!, [xj, zj] = pts[j]!;
    if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}
const local = (x: number, z: number, o: { x: number; z: number; rot: number }) => {
  const c = Math.cos(o.rot), s = Math.sin(o.rot), dx = x - o.x, dz = z - o.z;
  return [dx * c + dz * s, -dx * s + dz * c] as const;
};
const ringDist = (x: number, z: number, pts: Pt[]) => Math.min(distPolyline(x, z, pts), distSeg(x, z, pts[pts.length - 1]!, pts[0]!));

/** How far p is from the item's clickable shape (0 inside an area), or Infinity when it misses by more than tol. */
function hitDistance(key: ObjKey, item: Item, [x, z]: Pt, tol: number): number {
  if (isPointKey(key)) {
    const d = Math.hypot(x - (item as Pt)[0], z - (item as Pt)[1]);
    return d <= POINT_R[key]! + tol ? d : Infinity;
  }
  if (key === 'decor') {
    const o = item as { x: number; z: number; scale: number }, d = Math.hypot(x - o.x, z - o.z);
    return d <= 0.8 * o.scale + tol ? d : Infinity;
  }
  if (key === 'buildings' || key === 'fields' || key === 'fords') {
    const b = item as Patch, [lx, lz] = local(x, z, b);
    return Math.abs(lx) <= b.w / 2 + tol && Math.abs(lz) <= b.d / 2 + tol ? 0 : Infinity;
  }
  if (key === 'props') {
    const o = item as Prop, { r, w, d } = PROP_SHAPES[o.kind];
    if (r !== undefined) { const dist = Math.hypot(x - o.x, z - o.z); return dist <= r + tol ? dist : Infinity; }
    const [lx, lz] = local(x, z, o);
    return Math.abs(lx) <= w! / 2 + tol && Math.abs(lz) <= d! / 2 + tol ? 0 : Infinity;
  }
  if (key === 'brush') {
    const b = item as { x: number; z: number; rx: number; rz: number; rot: number };
    const [lx, lz] = local(x, z, b);
    return (lx / (b.rx + tol)) ** 2 + (lz / (b.rz + tol)) ** 2 <= 1 ? 0 : Infinity;
  }
  const pts = vertices(key, item)!;
  if (isClosed(key, item)) {
    const d = ringDist(x, z, pts);
    return inPolygon(x, z, pts) ? 0 : d <= tol + 0.5 ? d : Infinity;
  }
  const width = key === 'streams' || key === 'roads' ? (item as { width: number }).width / 2 : 0.6;
  const d = distPolyline(x, z, pts);
  return d <= width + tol ? d : Infinity;
}

/* What wins where shapes overlap: small things before the areas they sit in. */
const LAYERS: ObjKey[][] = [
  ['trees', 'rocks', 'pillars', 'camps', 'decor', 'props', 'bridges'],
  ['buildings', 'fords'],
  ['walls', 'cliffs', 'streams', 'roads'],
  ['ponds'],
  ['brush', 'fields'],
];

/** The authored object under p (tol in metres, from the zoom), or null. */
export function pick(map: MapData, p: Pt, tol = 0.3): Ref | null {
  let special: { ref: Ref; d: number } | null = null;
  for (const kind of ['base', 'side', 'den'] as const) {
    const [sx, sz] = specialPos(map, kind), d = Math.hypot(p[0] - sx, p[1] - sz);
    if (d <= SPECIAL_R + tol && (!special || d < special.d)) special = { ref: { kind }, d };
  }
  if (special) return special.ref;
  for (const layer of LAYERS) {
    let best: ObjRef | null = null, bestD = Infinity;
    for (const key of layer) for (const part of ['half', 'center'] as const) {
      const items = map[part][key] as Item[];
      for (let index = 0; index < items.length; index++) {
        const d = hitDistance(key, items[index]!, p, tol);
        if (d < bestD) { best = { kind: 'obj', part, key, index }; bestD = d; }
      }
    }
    if (best) return best;
  }
  return null;
}

/** Index of the selected item's vertex within tol of p, or -1. */
export function pickVertex(map: MapData, ref: ObjRef, [x, z]: Pt, tol: number): number {
  const pts = vertices(ref.key, getItem(map, ref));
  if (!pts) return -1;
  let best = -1, bestD = tol;
  pts.forEach(([vx, vz], i) => { const d = Math.hypot(x - vx, z - vz); if (d <= bestD) { best = i; bestD = d; } });
  return best;
}

/** Index of the segment start nearest p within tol (the closing edge of a polygon counts), or -1. */
export function pickSegment(map: MapData, ref: ObjRef, [x, z]: Pt, tol: number): number {
  const item = getItem(map, ref), pts = vertices(ref.key, item);
  if (!pts) return -1;
  const n = isClosed(ref.key, item) ? pts.length : pts.length - 1;
  let best = -1, bestD = tol;
  for (let i = 0; i < n; i++) {
    const d = distSeg(x, z, pts[i]!, pts[(i + 1) % pts.length]!);
    if (d <= bestD) { best = i; bestD = d; }
  }
  return best;
}

/* ---- validation for the live preview ---- */

/** Every problem the validator finds, never throwing: a half-built map is still being edited. */
export function checkMap(map: MapData): MapIssue[] {
  try {
    validateMap(map);
    return [];
  } catch (err) {
    if (err instanceof MapValidationError) return err.issues;
    return [{ path: '', code: 'error', params: { text: String(err) } }];
  }
}

/** The object a validator issue path points at, when it names one. */
export function refOfIssue(path: string): Ref | null {
  if (path === 'base' || path.startsWith('base[')) return { kind: 'base' };
  if (path.startsWith('points.side')) return { kind: 'side' };
  if (path.startsWith('den')) return { kind: 'den' };
  const m = /^(half|center)\.([a-z]+)\[(\d+)\]/.exec(path);
  if (!m) return null;
  return { kind: 'obj', part: m[1] as Part, key: m[2] as ObjKey, index: Number(m[3]) };
}

export const sameRef = (a: Ref | null, b: Ref | null) =>
  !!a && !!b && a.kind === b.kind && (a.kind !== 'obj' || (b.kind === 'obj' && a.part === b.part && a.key === b.key && a.index === b.index));

/* ---- new objects ---- */

export type PlaceStyle = { wall: WallStyle; building: BuildingStyle; decor: DecorKind; prop: PropKind; road: RoadKind; width: number; closedCliff: boolean };

/** A new single-click object at p; null for kinds drawn vertex by vertex. */
export function newItem(key: ObjKey, p: Pt, style: PlaceStyle): Item | null {
  const [x, z] = cmPt(p);
  if (isPointKey(key)) return [x, z];
  if (key === 'buildings') return { x, z, w: 6, d: 4, rot: 0, style: style.building };
  if (key === 'brush') return { x, z, rx: 4, rz: 2.5, rot: 0 };
  if (key === 'decor') return { x, z, kind: style.decor, rot: 0, scale: 1 };
  if (key === 'props') return { x, z, kind: style.prop, rot: 0 };
  if (key === 'fields') return { x, z, w: 8, d: 6, rot: 0 };
  return null;
}

/** A new ford on the stream nearest p: centred on the stream's line where it passes closest to p, laid
    across it (local z crossing the water, reaching 1.5 m onto each bank); null when the map has no stream. */
export function newFord(map: MapData, [x, z]: Pt): Patch | null {
  let best: { d: number; at: Pt; ux: number; uz: number; width: number } | null = null;
  for (const s of expandMap(map).objects.streams) {
    for (let i = 0; i < s.points.length - 1; i++) {
      const a = s.points[i]!, b = s.points[i + 1]!, vx = b[0] - a[0], vz = b[1] - a[1], len = Math.hypot(vx, vz);
      const t = Math.max(0, Math.min(1, ((x - a[0]) * vx + (z - a[1]) * vz) / (len * len)));
      const at: Pt = [a[0] + vx * t, a[1] + vz * t], d = Math.hypot(x - at[0], z - at[1]);
      if (!best || d < best.d) best = { d, at, ux: vx / len, uz: vz / len, width: s.width };
    }
  }
  if (!best) return null;
  const [fx, fz] = cmPt(best.at);
  return { x: fx, z: fz, w: 3, d: cm(best.width + 3), rot: turn(Math.atan2(best.uz, best.ux)) };
}

/** A finished vertex-by-vertex draft as an item, or null when it has too few points. */
export function newShape(key: ObjKey, pts: Pt[], style: PlaceStyle): Item | null {
  const closed = key === 'ponds' || (key === 'cliffs' && style.closedCliff);
  if (pts.length < (closed ? 3 : 2)) return null;
  const points = pts.map(cmPt);
  if (key === 'ponds') return points;
  if (key === 'walls') return { points, style: style.wall };
  if (key === 'cliffs') return { points, closed };
  if (key === 'roads') return { points, width: style.width, kind: style.road };
  return { points, width: style.width };
}
